import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, Trash2, Calendar, BookOpen, GraduationCap, Layers, Star } from "lucide-react";
import { AdminShell } from "@/components/admin/admin-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { requireSchoolAdminOrOnboard, useSchoolContext } from "@/lib/school-context";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type Grade = Database["public"]["Enums"]["grade_level"];
const ALL_GRADES: Grade[] = ["P1","P2","P3","P4","P5","P6","P7","S1","S2","S3","S4","S5","S6"];

export const Route = createFileRoute("/admin/academics")({
  ssr: false,
  beforeLoad: () => requireSchoolAdminOrOnboard(),
  head: () => ({ meta: [{ title: "Academics — MAKAI" }] }),
  component: AcademicsPage,
});

function AcademicsPage() {
  return (
    <AdminShell title="Academic management" subtitle="Years, terms, classes, streams and subjects.">
      <Tabs defaultValue="years">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="years"><Calendar className="mr-1.5 h-4 w-4" /> Years &amp; terms</TabsTrigger>
          <TabsTrigger value="classes"><GraduationCap className="mr-1.5 h-4 w-4" /> Classes</TabsTrigger>
          <TabsTrigger value="streams"><Layers className="mr-1.5 h-4 w-4" /> Streams</TabsTrigger>
          <TabsTrigger value="subjects"><BookOpen className="mr-1.5 h-4 w-4" /> Subjects</TabsTrigger>
        </TabsList>
        <TabsContent value="years" className="mt-6"><YearsAndTerms /></TabsContent>
        <TabsContent value="classes" className="mt-6"><Classes /></TabsContent>
        <TabsContent value="streams" className="mt-6"><Streams /></TabsContent>
        <TabsContent value="subjects" className="mt-6"><Subjects /></TabsContent>
      </Tabs>
    </AdminShell>
  );
}

// ============================ YEARS & TERMS ============================
function YearsAndTerms() {
  const { active } = useSchoolContext();
  const [years, setYears] = useState<{ id: string; name: string; start_date: string; end_date: string; is_current: boolean }[]>([]);
  const [terms, setTerms] = useState<Record<string, { id: string; name: string; start_date: string; end_date: string; is_current: boolean }[]>>({});

  const load = useCallback(async () => {
    if (!active) return;
    const { data: ys } = await supabase.from("academic_years").select("*").eq("school_id", active.school_id).order("start_date", { ascending: false });
    setYears(ys ?? []);
    const { data: ts } = await supabase.from("terms").select("*").eq("school_id", active.school_id).order("start_date");
    const map: typeof terms = {};
    (ts ?? []).forEach((t) => { (map[t.academic_year_id] ??= []).push(t); });
    setTerms(map);
  }, [active]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><YearDialog onSaved={load} /></div>
      {years.length === 0 && <Empty body="No academic years yet. Create one to start adding terms." />}
      {years.map((y) => (
        <div key={y.id} className="rounded-2xl border border-border bg-card p-5 shadow-soft">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-display text-lg font-bold">{y.name}</h3>
                {y.is_current && <Badge className="bg-primary text-primary-foreground">Current</Badge>}
              </div>
              <p className="text-xs text-muted-foreground">{y.start_date} → {y.end_date}</p>
            </div>
            <div className="flex gap-1.5">
              {!y.is_current && (
                <Button size="sm" variant="outline" onClick={async () => {
                  if (!active) return;
                  await supabase.from("academic_years").update({ is_current: false }).eq("school_id", active.school_id);
                  const { error } = await supabase.from("academic_years").update({ is_current: true }).eq("id", y.id);
                  if (error) toast.error(error.message); else { toast.success("Set as current"); load(); }
                }}><Star className="h-3.5 w-3.5" /></Button>
              )}
              <TermDialog yearId={y.id} schoolId={active!.school_id} onSaved={load} />
              <Button size="sm" variant="outline" onClick={async () => {
                if (!confirm("Delete this year and its terms?")) return;
                const { error } = await supabase.from("academic_years").delete().eq("id", y.id);
                if (error) toast.error(error.message); else { toast.success("Deleted"); load(); }
              }}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
          <div className="mt-4 grid gap-2 sm:grid-cols-3">
            {(terms[y.id] ?? []).map((t) => (
              <div key={t.id} className="rounded-xl border border-border bg-background p-3">
                <div className="flex items-center justify-between">
                  <div className="font-semibold">{t.name}</div>
                  {t.is_current && <Badge variant="secondary">Now</Badge>}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{t.start_date} → {t.end_date}</div>
              </div>
            ))}
            {(terms[y.id] ?? []).length === 0 && <div className="col-span-full text-xs text-muted-foreground">No terms yet — add Term 1, Term 2 and Term 3 above.</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function YearDialog({ onSaved }: { onSaved: () => void }) {
  const { active } = useSchoolContext();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: String(new Date().getFullYear()), start_date: "", end_date: "", is_current: true });
  const [busy, setBusy] = useState(false);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!active) return;
    setBusy(true);
    if (form.is_current) await supabase.from("academic_years").update({ is_current: false }).eq("school_id", active.school_id);
    const { error } = await supabase.from("academic_years").insert({ ...form, school_id: active.school_id });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Year added"); setOpen(false); onSaved();
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90"><Plus className="mr-1.5 h-4 w-4" /> New academic year</Button></DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>New academic year</DialogTitle></DialogHeader>
        <form className="space-y-3" onSubmit={submit}>
          <Field label="Name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="2026" required /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date *"><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required /></Field>
            <Field label="End date *"><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} required /></Field>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border p-3"><Label>Mark as current</Label><Switch checked={form.is_current} onCheckedChange={(v) => setForm({ ...form, is_current: v })} /></div>
          <DialogFooter><Button type="submit" disabled={busy} className="bg-primary hover:bg-primary/90">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TermDialog({ yearId, schoolId, onSaved }: { yearId: string; schoolId: string; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "Term 1", start_date: "", end_date: "", is_current: false });
  const [busy, setBusy] = useState(false);
  async function submit(e: React.FormEvent) {
    e.preventDefault(); setBusy(true);
    if (form.is_current) await supabase.from("terms").update({ is_current: false }).eq("school_id", schoolId);
    const { error } = await supabase.from("terms").insert({ ...form, school_id: schoolId, academic_year_id: yearId });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Term added"); setOpen(false); onSaved();
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline"><Plus className="mr-1.5 h-3.5 w-3.5" /> Term</Button></DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>New term</DialogTitle></DialogHeader>
        <form className="space-y-3" onSubmit={submit}>
          <Field label="Name *">
            <Select value={form.name} onValueChange={(v) => setForm({ ...form, name: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["Term 1","Term 2","Term 3"].map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date *"><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required /></Field>
            <Field label="End date *"><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} required /></Field>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border p-3"><Label>Mark as current</Label><Switch checked={form.is_current} onCheckedChange={(v) => setForm({ ...form, is_current: v })} /></div>
          <DialogFooter><Button type="submit" disabled={busy} className="bg-primary hover:bg-primary/90">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================ CLASSES ============================
function Classes() {
  const { active } = useSchoolContext();
  const [rows, setRows] = useState<{ id: string; name: string; grade: Grade; level: string }[]>([]);

  const load = useCallback(async () => {
    if (!active) return;
    const { data } = await supabase.from("classes").select("*").eq("school_id", active.school_id).order("order_index");
    setRows((data ?? []) as typeof rows);
  }, [active]);
  useEffect(() => { load(); }, [load]);

  async function seedAll() {
    if (!active) return;
    if (!confirm("Create all classes P1–P7 and S1–S6?")) return;
    const existing = new Set(rows.map((r) => r.grade));
    const inserts = ALL_GRADES.filter((g) => !existing.has(g)).map((g, i) => ({
      school_id: active.school_id,
      grade: g, name: g,
      level: g.startsWith("P") ? "primary" : "secondary",
      order_index: ALL_GRADES.indexOf(g),
    }));
    if (inserts.length === 0) { toast.info("All classes already exist"); return; }
    const { error } = await supabase.from("classes").insert(inserts);
    if (error) toast.error(error.message); else { toast.success(`Added ${inserts.length} classes`); load(); }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2"><Button variant="outline" onClick={seedAll}>Seed P1–S6</Button><ClassDialog onSaved={load} /></div>
      {rows.length === 0 && <Empty body="No classes yet. Use “Seed P1–S6” for the standard Ugandan structure or add a single class." />}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {rows.map((c) => (
          <div key={c.id} className="rounded-2xl border border-border bg-card p-4 shadow-soft">
            <div className="flex items-center justify-between">
              <div className="font-display text-lg font-bold">{c.name}</div>
              <Badge variant={c.level === "primary" ? "secondary" : "outline"} className="capitalize">{c.level}</Badge>
            </div>
            <div className="mt-1 text-xs text-muted-foreground">Grade {c.grade}</div>
            <Button size="sm" variant="ghost" className="mt-3 px-0 text-destructive hover:text-destructive" onClick={async () => {
              if (!confirm("Delete this class?")) return;
              const { error } = await supabase.from("classes").delete().eq("id", c.id);
              if (error) toast.error(error.message); else load();
            }}><Trash2 className="mr-1 h-3.5 w-3.5" /> Remove</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ClassDialog({ onSaved }: { onSaved: () => void }) {
  const { active } = useSchoolContext();
  const [open, setOpen] = useState(false);
  const [grade, setGrade] = useState<Grade>("P1");
  const [busy, setBusy] = useState(false);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!active) return;
    setBusy(true);
    const { error } = await supabase.from("classes").insert({
      school_id: active.school_id, grade, name: grade,
      level: grade.startsWith("P") ? "primary" : "secondary",
      order_index: ALL_GRADES.indexOf(grade),
    });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Class added"); setOpen(false); onSaved();
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90"><Plus className="mr-1.5 h-4 w-4" /> Class</Button></DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Add class</DialogTitle></DialogHeader>
        <form className="space-y-3" onSubmit={submit}>
          <Field label="Grade">
            <Select value={grade} onValueChange={(v) => setGrade(v as Grade)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{ALL_GRADES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <DialogFooter><Button type="submit" disabled={busy} className="bg-primary hover:bg-primary/90">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================ STREAMS ============================
function Streams() {
  const { active } = useSchoolContext();
  const [rows, setRows] = useState<{ id: string; name: string; capacity: number; class: { name: string } | null; campus: { name: string } | null }[]>([]);
  const [classes, setClasses] = useState<{ id: string; name: string }[]>([]);
  const [campuses, setCampuses] = useState<{ id: string; name: string }[]>([]);

  const load = useCallback(async () => {
    if (!active) return;
    const [s, c, cm] = await Promise.all([
      supabase.from("streams").select("id,name,capacity,class:classes(name),campus:campuses(name)").eq("school_id", active.school_id).order("created_at"),
      supabase.from("classes").select("id,name").eq("school_id", active.school_id).order("order_index"),
      supabase.from("campuses").select("id,name").eq("school_id", active.school_id),
    ]);
    setRows((s.data ?? []) as typeof rows);
    setClasses(c.data ?? []);
    setCampuses(cm.data ?? []);
  }, [active]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><StreamDialog classes={classes} campuses={campuses} onSaved={load} /></div>
      {rows.length === 0 ? <Empty body="No streams yet. Add a stream (e.g. P5 East) for each class group." /> : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-card shadow-soft">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr><th className="px-4 py-2.5">Class</th><th className="px-4 py-2.5">Stream</th><th className="px-4 py-2.5">Campus</th><th className="px-4 py-2.5">Capacity</th><th className="px-4 py-2.5 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-4 py-2.5 font-semibold">{r.class?.name ?? "—"}</td>
                  <td className="px-4 py-2.5">{r.name}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{r.campus?.name ?? "—"}</td>
                  <td className="px-4 py-2.5">{r.capacity}</td>
                  <td className="px-4 py-2.5 text-right">
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={async () => {
                      if (!confirm("Delete stream?")) return;
                      const { error } = await supabase.from("streams").delete().eq("id", r.id);
                      if (error) toast.error(error.message); else load();
                    }}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StreamDialog({ classes, campuses, onSaved }: { classes: { id: string; name: string }[]; campuses: { id: string; name: string }[]; onSaved: () => void }) {
  const { active } = useSchoolContext();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<{ class_id: string; name: string; capacity: number; campus_id: string | null }>({ class_id: "", name: "", capacity: 60, campus_id: null });
  const [busy, setBusy] = useState(false);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!active || !form.class_id) { toast.error("Pick a class"); return; }
    setBusy(true);
    const { error } = await supabase.from("streams").insert({
      school_id: active.school_id, class_id: form.class_id, name: form.name, capacity: form.capacity, campus_id: form.campus_id || null,
    });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Stream added"); setOpen(false); onSaved();
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" disabled={classes.length === 0}><Plus className="mr-1.5 h-4 w-4" /> Stream</Button></DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>New stream</DialogTitle></DialogHeader>
        <form className="space-y-3" onSubmit={submit}>
          <Field label="Class *">
            <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })}>
              <SelectTrigger><SelectValue placeholder="Pick a class" /></SelectTrigger>
              <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Stream name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="East" required /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Capacity"><Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: Number(e.target.value) })} /></Field>
            <Field label="Campus">
              <Select value={form.campus_id ?? "_none"} onValueChange={(v) => setForm({ ...form, campus_id: v === "_none" ? null : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— None —</SelectItem>
                  {campuses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter><Button type="submit" disabled={busy} className="bg-primary hover:bg-primary/90">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================ SUBJECTS ============================
function Subjects() {
  const { active } = useSchoolContext();
  const [rows, setRows] = useState<{ id: string; code: string; name: string; level: string; is_core: boolean }[]>([]);
  const load = useCallback(async () => {
    if (!active) return;
    const { data } = await supabase.from("subjects_catalog").select("*").eq("school_id", active.school_id).order("name");
    setRows((data ?? []) as typeof rows);
  }, [active]);
  useEffect(() => { load(); }, [load]);

  async function seed() {
    if (!active) return;
    const defaults = [
      { code: "MTH", name: "Mathematics", level: "both", is_core: true },
      { code: "ENG", name: "English", level: "both", is_core: true },
      { code: "SCI", name: "Science", level: "primary", is_core: true },
      { code: "SST", name: "Social Studies", level: "primary", is_core: true },
      { code: "PHY", name: "Physics", level: "secondary", is_core: false },
      { code: "CHM", name: "Chemistry", level: "secondary", is_core: false },
      { code: "BIO", name: "Biology", level: "secondary", is_core: false },
      { code: "HIS", name: "History", level: "secondary", is_core: false },
      { code: "GEO", name: "Geography", level: "secondary", is_core: false },
    ];
    const existing = new Set(rows.map((r) => r.code));
    const inserts = defaults.filter((d) => !existing.has(d.code)).map((d) => ({ ...d, school_id: active.school_id }));
    if (inserts.length === 0) { toast.info("Defaults already added"); return; }
    const { error } = await supabase.from("subjects_catalog").insert(inserts);
    if (error) toast.error(error.message); else { toast.success(`Added ${inserts.length} subjects`); load(); }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2"><Button variant="outline" onClick={seed}>Seed defaults</Button><SubjectDialog onSaved={load} /></div>
      {rows.length === 0 ? <Empty body="No subjects yet. Seed the defaults or add one." /> : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((s) => (
            <div key={s.id} className="rounded-2xl border border-border bg-card p-4 shadow-soft">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-base font-bold">{s.name}</div>
                  <div className="text-xs text-muted-foreground">{s.code} · {s.level}</div>
                </div>
                {s.is_core && <Badge>Core</Badge>}
              </div>
              <Button size="sm" variant="ghost" className="mt-3 px-0 text-destructive hover:text-destructive" onClick={async () => {
                if (!confirm("Delete subject?")) return;
                const { error } = await supabase.from("subjects_catalog").delete().eq("id", s.id);
                if (error) toast.error(error.message); else load();
              }}><Trash2 className="mr-1 h-3.5 w-3.5" /> Remove</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SubjectDialog({ onSaved }: { onSaved: () => void }) {
  const { active } = useSchoolContext();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", level: "both" as "primary"|"secondary"|"both", is_core: true });
  const [busy, setBusy] = useState(false);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!active) return;
    setBusy(true);
    const { error } = await supabase.from("subjects_catalog").insert({ ...form, code: form.code.toUpperCase(), school_id: active.school_id });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Subject added"); setOpen(false); onSaved();
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90"><Plus className="mr-1.5 h-4 w-4" /> Subject</Button></DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>New subject</DialogTitle></DialogHeader>
        <form className="space-y-3" onSubmit={submit}>
          <Field label="Code *"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} maxLength={6} required /></Field>
          <Field label="Name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></Field>
          <Field label="Level">
            <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v as typeof form.level })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="primary">Primary</SelectItem><SelectItem value="secondary">Secondary</SelectItem><SelectItem value="both">Both</SelectItem></SelectContent>
            </Select>
          </Field>
          <div className="flex items-center justify-between rounded-lg border border-border p-3"><Label>Core subject</Label><Switch checked={form.is_core} onCheckedChange={(v) => setForm({ ...form, is_core: v })} /></div>
          <DialogFooter><Button type="submit" disabled={busy} className="bg-primary hover:bg-primary/90">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (<div className={"space-y-1.5 " + (className ?? "")}><Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>);
}
function Empty({ body }: { body: string }) { return <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">{body}</div>; }
